import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Tooltip, Table, message, Spin, Empty } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  EyeOutlined,
  ReloadOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import {
  GUIDE_ENGAGE,
  TOPIC_POST_EVENTS,
} from "../../../../../constants/gettingStarted";
import {
  isMobile,
  sortObjectByString,
  sortObjectByNumber,
} from "../../../../../utils/helper";
import ScreenHeader from "../../../../../CommonComponents/ScreenHeader";
import ChantButton from "../../../../../CommonComponents/ChantButton/ChantButton";
import TabNav from "../../../../../CommonComponents/TabNav/TabNav";
import { IconContainer } from "../../../../../CommonComponents/index.js";
import ChantPopConfirm from "../../../../../CommonComponents/ChantTooltip/ChantPopConfirm.jsx";
import {
  updateEventNotifications,
  getEventsFromDb,
} from "../../../../../services/firebaseService/endPoints/admin/content/events.js";
import logger from "../../../../../utils/logger.js";
import { deleteEventService } from "../../../../../services/firebaseService/endPoints/admin/content/events/form.js";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../../store/adminData/adminData";
import { toast } from "react-toastify";
import useDashboardInfo from "../../../../../hooks/useDashboardInfo.js";

dayjs.extend(utc);

const EventsList = () => {
  const navigate = useNavigate();

  const groupData = useAdminDataStore(selectCurrentGroupData);
  const { isParent } = useDashboardInfo();

  // Load tab from localStorage on mount, default to "current"
  const getStoredTab = useCallback((groupId) => {
    if (!groupId) return "current";
    const stored = localStorage.getItem(`eventsListTab_${groupId}`);
    return stored === "past" ? "past" : "current";
  }, []);

  const [tab, setTab] = useState(() => getStoredTab(groupData?.id));

  const {
    data: events = [],
    isLoading: loading,
    error,
    refetch: refreshEvents,
  } = useQuery({
    queryKey: ["events", groupData?.id, tab],
    queryFn: async () => {
      if (!groupData?.id) {
        return [];
      }
      return await getEventsFromDb(
        groupData.id,
        tab,
        (groupData?.isParent && isParent) || false,
      );
    },
    enabled: !!groupData?.id,
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
  });

  // Update getting started status when events are loaded
  useEffect(() => {
    if (events?.length > 0) {
      useAdminDataStore.getState().setGettingStartedItemStatus(
        groupData.id,
        GUIDE_ENGAGE,
        TOPIC_POST_EVENTS,
        true,
      );
    }
  }, [events, groupData.id]);

  // Handle errors
  useEffect(() => {
    if (error) {
      toast.error("Failed to load events");
    }
  }, [error]);

  const handleTabChange = useCallback(
    (key) => {
      setTab(key);
      // Save tab to localStorage
      if (groupData?.id) {
        localStorage.setItem(`eventsListTab_${groupData.id}`, key);
      }
    },
    [groupData?.id],
  );

  // Update tab from localStorage when group changes
  useEffect(() => {
    if (groupData?.id) {
      const storedTab = getStoredTab(groupData.id);
      setTab(storedTab);
    }
  }, [groupData?.id, getStoredTab]);

  const handleFormActions = useCallback(
    (mode, contentId) => {
      if (mode === "edit" || mode === "view" || mode === "copy") {
        navigate(`/admin/content/${mode}/${groupData.id}/${contentId}`);
      } else if (mode === "create") {
        navigate(`/admin/content/${mode}/${groupData.id}`);
      }
    },
    [navigate, groupData.id],
  );

  // Handle delete content
  const handleDeleteContent = useCallback(
    async (data) => {
      try {
        const msg = message.loading("Deleting content", 0);

        await deleteEventService(data.id, groupData.id, {
          deleteImage: !!data.details.image,
        });

        updateEventNotifications(
          { notifications: [null] },
          data.notifications,
          groupData.id,
          data.id,
          true,
        );
        msg();
        message.success("Successfully deleted content");
        // Refresh events list after deletion
        refreshEvents();
      } catch (err) {
        logger.error("Failed to delete content: ", err);
        message.error("Failed to delete content");
      }
    },
    [groupData.id, refreshEvents],
  );

  const getTimeFromMatchName = useCallback((matchName) => {
    // Try new format first: (14th Jan, 2026)
    let match = matchName?.match(/\((\d{1,2}[a-z]{2} [A-Za-z]+, \d{4})\)/);
    if (!match) {
      match = matchName?.match(/\((\d{1,2}[a-z]{2}-[A-Za-z]+-\d{2})\)/);
    }
    return match ? match[1] : null;
  }, []);

  // Table columns definition
  const columns = useMemo(
    () => [
      {
        title: window.screen.width > 500 ? "Action" : "",
        className: "header-white",
        width: 150,
        render: (data) => {
          if (data.loading) {
            return {
              children: <></>,
              colSpan: 3,
            };
          }
          return (
            <div
              style={{
                display: "flex",
                gap: "12px",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Tooltip title="edit">
                <div
                  className="cursor-pointer"
                  onClick={() => handleFormActions("edit", data.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <EditOutlined
                    style={{ fontSize: "20px", color: "#8c8c8c" }}
                  />
                </div>
              </Tooltip>
              <Tooltip title="copy">
                <div
                  className="cursor-pointer"
                  onClick={() => handleFormActions("copy", data.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CopyOutlined
                    style={{ fontSize: "18px", color: "#8c8c8c" }}
                  />
                </div>
              </Tooltip>
              <ChantPopConfirm
                title="Delete Event"
                description="Are you sure you want to delete this event? This action cannot be undone."
                onConfirm={() => handleDeleteContent(data)}
                onCancel={() => {}}
                okText="Delete"
                cancelText="Cancel"
              >
                <Tooltip title="delete">
                  <div
                    className="cursor-pointer"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <DeleteOutlined
                      style={{ fontSize: "20px", color: "#8c8c8c" }}
                    />
                  </div>
                </Tooltip>
              </ChantPopConfirm>
              {tab === "past" && (
                <Tooltip title="view">
                  <div
                    className="cursor-pointer"
                    onClick={() => handleFormActions("view", data.id)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <EyeOutlined
                      style={{ fontSize: "20px", color: "#8c8c8c" }}
                    />
                  </div>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        title: "Type",
        width: window.screen.width > 500 ? 160 : 100,
        className: "header-white",
        key: "type",
        dataIndex: "type",
      },
      {
        title: "Match",
        width: 150,
        className: "header-white",
        key: "matchName",
        dataIndex: "awayTeam",
        sorter: (a, b) => sortObjectByString(a, b, "matchName"),
        render: (data, record) => {
          return (
            <span>
              {data?.team_name} <br />
              {record?.matchDate
                ? dayjs.unix(record?.matchDate).format("Do MMM, YYYY")
                : getTimeFromMatchName(record?.matchName)}
            </span>
          );
        },
      },
      {
        title: "Title",
        key: "title",
        align: "left",
        className: "header-white",
        dataIndex: "title",
        sorter: (a, b) => sortObjectByString(a, b, "title"),
      },
      {
        title: "Event Points",
        key: "eventPoints",
        align: "left",
        className: "header-white",
        render: (data) => {
          const { points = undefined, season = undefined } =
            data?.checkIn || {};
          return <span>{points && season ? `${points} / ${season}` : ""}</span>;
        },
      },
      {
        title: "Volunteer",
        key: "slotName",
        align: "left",
        className: "header-white",
        render: (data) => {
          const values = Object.values(data?.volunteerRequests || {})
            .sort((a, b) => a?.sortIndex - b?.sortIndex)
            .map((item) => ({
              title: item?.title,
              points: item?.points,
              slots: item?.slotCount,
            }));

          return (
            <>
              {values.map((val, index) => (
                <div key={index}>
                  {`${val.title}: ${val.slots} slots / ${val.points}pts`}
                </div>
              ))}
            </>
          );
        },
      },
      {
        title: "Date",
        key: "details",
        dataIndex: "details",
        className: "header-white",
        sorter: (a, b) =>
          sortObjectByNumber(
            a?.details || {},
            b?.details || {},
            "eventStartDate",
          ),
        render: (details) => {
          let startDate = "",
            endDate = "";
          if (details?.eventStartDate) {
            startDate = dayjs.unix(details.eventStartDate);
            startDate = startDate.isValid()
              ? startDate.format("Do MMM, YYYY")
              : "";
          }
          if (details?.eventEndDate && details.eventEndDate !== 9999999999) {
            endDate = dayjs.unix(details.eventEndDate);
            endDate = endDate.isValid() ? endDate.format("-Do MMM, YYYY") : "";
          }
          return (
            <span>
              {startDate === endDate.slice(1)
                ? `${startDate}${endDate}`
                : `${startDate}${endDate}`}
            </span>
          );
        },
      },
      {
        title: "Location",
        key: "details.location",
        className: "header-white",
        sorter: (a, b) => {
          const getLocationString = (rec) => {
            if (rec.location) {
              return rec.location;
            } else {
              let allLocations = [];
              if (rec.mapsData) {
                allLocations.push(rec.mapsData);
              }
              allLocations.push(...(rec.additionalLocations || []));
              return allLocations
                .map((loc) => loc?.name || "")
                .filter((locName) => locName)
                .join(",");
            }
          };
          return sortObjectByString(
            { location: getLocationString(a) },
            { location: getLocationString(b) },
            "location",
          );
        },
        render: (rec) => {
          if (rec.location) {
            return <span>{rec.location}</span>;
          } else {
            let allLocations = [];
            if (rec.mapsData) {
              allLocations.push(rec.mapsData);
            }
            allLocations.push(...(rec.additionalLocations || []));

            let location = allLocations
              .map((loc) => loc?.name || "")
              .filter((locName) => locName)
              .join(",");
            return <span>{location}</span>;
          }
        },
      },
      {
        title: "RSVP",
        key: "rsvp",
        dataIndex: "rsvp",
        className: "header-white",
        sorter: (a, b) => {
          const getRsvpTotal = (rsvp) => {
            if (!rsvp) return 0;
            return (rsvp.attending || 0) + (rsvp.mayBe || 0) + (rsvp.no || 0);
          };
          return sortObjectByNumber(
            { total: getRsvpTotal(a.rsvp) },
            { total: getRsvpTotal(b.rsvp) },
            "total",
          );
        },
        render: (rsvp) => {
          return (
            <>
              <span className="color-green">
                {rsvp ? rsvp.attending || 0 : 0}
              </span>
              &nbsp;|&nbsp;
              <span className="color-yellow">{rsvp ? rsvp.mayBe || 0 : 0}</span>
              &nbsp;|&nbsp;
              <span className="color-red">{rsvp ? rsvp.no || 0 : 0}</span>
            </>
          );
        },
      },
      {
        title: "Show on home screen",
        width: 300,
        key: "showOnHomePage",
        className: "header-white",
        sorter: (a, b) => sortObjectByNumber(a, b, "showOnHomePageStart"),
        render: (data) => {
          if (data.loading) return { props: { colSpan: 0 } };
          let showOnHomePageStart = "",
            showOnHomePageEnd = "",
            color = "";
          if (data.showOnHomePageStart) {
            showOnHomePageStart = dayjs.unix(data.showOnHomePageStart);
            if (showOnHomePageStart.unix() < Date.now() / 1000) color = "green";
            showOnHomePageStart = showOnHomePageStart.isValid()
              ? showOnHomePageStart.format("Do MMM")
              : "";
          }

          if (data.showOnHomePageEnd) {
            showOnHomePageEnd = dayjs.unix(data.showOnHomePageEnd);
            color =
              "green" && showOnHomePageEnd.unix() > Date.now() / 1000
                ? (color = "green")
                : (color = "");
            showOnHomePageEnd = showOnHomePageEnd.isValid()
              ? showOnHomePageEnd.format("-Do MMM")
              : "";
          }

          return (
            <span style={{ color }}>
              {showOnHomePageStart}
              {showOnHomePageEnd}
            </span>
          );
        },
        dataIndex: "",
      },
      {
        title: "Notification1",
        className: "header-white",
        key: "notifications[0]",
        render: (data) => {
          let date = data.notifications?.[0];
          return date && dayjs.utc(date).isValid()
            ? dayjs.utc(date).format("Do MMM, YYYY")
            : "";
        },
      },
      {
        title: "Notification2",
        key: "notifications[1]",
        className: "header-white",
        render: (data) => {
          let date = data.notifications?.[1];
          return date && dayjs.utc(date).isValid()
            ? dayjs.utc(date).format("Do MMM, YYYY")
            : "";
        },
      },
      {
        title: "Notification3",
        key: "notifications[2]",
        className: "header-white",
        render: (data) => {
          let date = data.notifications?.[2];
          return date && dayjs.utc(date).isValid()
            ? dayjs.utc(date).format("Do MMM, YYYY")
            : "";
        },
      },
    ],
    [tab, handleFormActions, handleDeleteContent, getTimeFromMatchName],
  );

  const tabItems = [
    {
      key: "current",
      label: "Current",
    },
    {
      key: "past",
      label: "Past",
    },
  ];

  return (
    <div
      className="mx-auto"
      style={{ padding: "12px", display: "flex", flexDirection: "column" }}
    >
      <div
        style={{
          fontSize: "15px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <ScreenHeader
          title="News / Events"
          elementCount={events?.length}
          actions={
            <div style={{ display: "flex", gap: "12px" }}>
              {isMobile() ? (
                <IconContainer
                  onClick={() => {
                    navigate("/admin/content/create");
                  }}
                  iconComponent={<PlusOutlined />}
                  iconStyle={{ fontSize: "16px" }}
                  disabled={loading}
                />
              ) : (
                <ChantButton
                  onClick={() => {
                    navigate("/admin/content/create");
                  }}
                  disabled={loading}
                >
                  Create News/Event
                </ChantButton>
              )}

              <ChantPopConfirm
                title="Refresh content?"
                onConfirm={refreshEvents}
                onCancel={() => {}}
                okText="Yes"
                cancelText="No"
              >
                <IconContainer
                  iconComponent={<ReloadOutlined />}
                  iconStyle={{ fontSize: "20px" }}
                  disabled={loading}
                />
              </ChantPopConfirm>
            </div>
          }
        />

        <div style={{ paddingTop: "2px" }}>
          <TabNav
            items={tabItems}
            activeKey={tab}
            handleTabChange={handleTabChange}
          />
        </div>

        {loading && events.length === 0 ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
            }}
          >
            <Spin size="large" />
          </div>
        ) : events.length === 0 && !loading ? (
          <Empty description="No events found" style={{ margin: "40px 0" }} />
        ) : (
          <Table
            bordered
            columns={columns}
            loading={loading}
            bodyStyle={{
              fontSize: window.screen.width > 500 ? "18px" : "12px",
              backgroundColor: "#ffffff",
            }}
            dataSource={events}
            pagination={{
              position: ["bottomLeft"],
              defaultPageSize: 100,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
            }}
            rowKey="id"
            key="id"
            scroll={{ x: "max-content" }}
          />
        )}
      </div>
    </div>
  );
};

export default EventsList;
